import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Calendar, MapPin, Users, ExternalLink } from "lucide-react";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { displayAdminEventLabel } from "@/lib/datetime";

interface EventPreviewDialogProps {
  eventId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const EventPreviewDialog = ({
  eventId,
  open,
  onOpenChange,
}: EventPreviewDialogProps) => {
  const { data: event, isLoading } = useQuery({
    queryKey: ["event-preview", eventId],
    queryFn: async () => {
      if (!eventId) return null;

      const { data, error } = await supabase
        .from("events")
        .select("id, title, description, venue, start_time, end_time, timezone, capacity, image_url, is_archived, is_canceled")
        .eq("id", eventId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!eventId && open,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Event Details</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-32 w-full rounded-lg" />
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        ) : event ? (
          <div className="space-y-4">
            {event.image_url && (
              <img
                src={event.image_url}
                alt={event.title}
                className="w-full h-32 object-cover rounded-lg"
              />
            )}

            <div>
              <h3 className="font-semibold text-lg">
                {displayAdminEventLabel(event.title, event.start_time, event.timezone || undefined)}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                {event.is_canceled && (
                  <Badge variant="destructive">Canceled</Badge>
                )}
                {event.is_archived && (
                  <Badge variant="secondary">Archived</Badge>
                )}
              </div>
            </div>

            <div className="space-y-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>
                  {format(new Date(event.start_time), "EEE, MMM d, yyyy 'at' HH:mm")}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                <span>{event.venue}</span>
              </div>

              <div className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                <span>Capacity: {event.capacity}</span>
              </div>
            </div>

            {event.description && (
              <p className="text-sm text-muted-foreground line-clamp-3">
                {event.description.replace(/<[^>]*>/g, "")}
              </p>
            )}

            <Button asChild className="w-full">
              <Link to={`/events/${event.id}`} onClick={() => onOpenChange(false)}>
                <ExternalLink className="h-4 w-4 mr-2" />
                View Full Event
              </Link>
            </Button>
          </div>
        ) : (
          <p className="text-muted-foreground text-center py-4">
            Event not found
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
};
